import React                 from 'react';
import PlaceModel            from '@models/map/PlaceModel';
import leftSidebarStore      from '@/TokamapEditor/stores/leftSidebarStore';
import currentEditorStore    from '@/TokamapEditor/stores/currentEditorStore';
import linkPlaceToShape      from '@/TokamapEditor/helpers/linkPlaceToShape';
import { message }           from '@widesk-ui/hooks/useMessage';

const DRAG_THRESHOLD_PX = 6;

/**
 * Raison pour laquelle une forme ne peut pas recevoir de point d'intérêt (ou `null` si elle le peut) :
 * une forme verrouillée (`Shape.locked`) ne doit pas être modifiée, et une forme image n'est pas
 * liable à un point d'intérêt.
 */
function getDropRefusal(shapeId: string): string | null {
	const shape = currentEditorStore.get?.getAllShapes().find(s => s.id === shapeId);

	if (!shape) return null;
	if (shape.locked) return 'Forme verrouillée';
	if (shape.shapeKind === 'image') return 'Forme image non liable';

	return null;
}

/**
 * Glisser-déposer d'un point d'intérêt (`PlaceListSidebar`) vers une forme de la carte, en souris
 * "manuelle" (mousedown/mousemove/mouseup) plutôt qu'en glisser-déposer HTML natif — le natif
 * (`draggable`/`dragstart`/`drop`) ne fonctionne pas ici : la ligne source vit dans le `Drawer`
 * d'Ant Design, qui anime son ouverture/fermeture via un `transform` CSS sur un ancêtre, et Chrome
 * n'émet alors plus `dragover`/`drop` en dehors de cet ancêtre (bug connu, indépendant de ce code).
 * Un seuil de déplacement (`DRAG_THRESHOLD_PX`) évite de déclencher le geste sur un simple clic
 * (qui doit continuer à sélectionner la ligne pour les actions de masse, voir `PlaceListSidebar`).
 *
 * La forme visée à la dépose n'est PAS déterminée par un hit-test écran→forme fait ici (une première
 * version convertissait `clientX`/`clientY` en repère carte à la main, avec `getBoundingClientRect()`
 * — ça a divergé subtilement de la conversion interne de `MapLibreAdapter`, ciblant parfois une
 * forme voisine de celle visée). On se branche à la place sur `"shape:hovered"` (voir
 * `adapters/types.ts`) : `MapLibreAdapter` le maintient déjà à jour via de vrais évènements
 * `mousemove` natifs sur son canvas, qui continuent d'arriver normalement pendant ce geste (la souris
 * physique bouge réellement) — on lit juste sa dernière valeur connue au relâchement.
 */
export default function startPlaceDrag(place: PlaceModel, downEvent: React.MouseEvent, imageSrc?: string) {
	if (downEvent.button !== 0) return;

	downEvent.preventDefault();

	const startX = downEvent.clientX;
	const startY = downEvent.clientY;

	let dragging = false;
	let ghost: HTMLDivElement | null = null;
	let ghostHint: HTMLDivElement | null = null;
	let hoveredShapeId: string | null = null;
	let dropRefusal: string | null = null;
	let unsubscribeHover: (() => void) | undefined;

	const moveGhostTo = (x: number, y: number) => {
		if (!ghost) return;
		ghost.style.left = `${x}px`;
		ghost.style.top = `${y}px`;
	};

	// Reflète en direct, sur le ghost lui-même, si une forme est actuellement visée (bordure verte +
	// texte d'aide qui change) — l'utilisateur sait ainsi avant même de relâcher le clic si le dépose
	// va fonctionner, plutôt que de le découvrir seulement au message d'avertissement/succès final.
	const updateGhostDropState = () => {
		if (!ghost || !ghostHint) return;
		const droppable = !!hoveredShapeId && !dropRefusal;
		ghost.style.borderColor = droppable ? '#52c41a' : (dropRefusal ? '#ff4d4f' : 'transparent');
		ghost.style.transform = `rotate(-3deg) scale(${droppable ? 1.06 : 1.02})`;
		ghostHint.textContent = droppable
			? 'Relâcher pour lier'
			: (dropRefusal ?? 'Déposer sur une forme');
	};

	const startDragging = () => {
		dragging = true;

		ghost = document.createElement('div');
		Object.assign(ghost.style, {
			position: 'fixed',
			zIndex: '2000',
			pointerEvents: 'none',
			display: 'flex',
			alignItems: 'center',
			gap: '8px',
			padding: '6px 12px 6px 6px',
			borderRadius: '10px',
			background: 'rgba(28, 28, 30, 0.92)',
			boxShadow: '0 6px 16px rgba(0, 0, 0, 0.35)',
			border: '2px solid transparent',
			color: '#fff',
			whiteSpace: 'nowrap',
			transform: 'rotate(-3deg) scale(1.02)',
			transition: 'border-color 0.15s ease, transform 0.1s ease',
		});

		const thumb = document.createElement('div');
		Object.assign(thumb.style, {
			width: '28px',
			height: '28px',
			borderRadius: '7px',
			flexShrink: '0',
		});
		if (imageSrc) {
			Object.assign(thumb.style, {
				backgroundImage: `url("${imageSrc.replace(/"/g, '%22')}")`,
				backgroundSize: 'cover',
				backgroundPosition: 'center',
			});
		} else {
			Object.assign(thumb.style, {
				background: 'var(--ant-color-primary)',
				color: '#fff',
				fontSize: '13px',
				fontWeight: '700',
				display: 'flex',
				alignItems: 'center',
				justifyContent: 'center',
			});
			thumb.textContent = place.label.charAt(0).toUpperCase();
		}
		ghost.appendChild(thumb);

		const texts = document.createElement('div');
		Object.assign(texts.style, {
			display: 'flex',
			flexDirection: 'column',
			gap: '1px',
		});

		const title = document.createElement('div');
		Object.assign(title.style, { fontSize: '13px', fontWeight: '600' });
		title.textContent = place.label;
		texts.appendChild(title);

		ghostHint = document.createElement('div');
		Object.assign(ghostHint.style, { fontSize: '11px', color: 'rgba(255, 255, 255, 0.65)' });
		ghostHint.textContent = 'Déposer sur une forme';
		texts.appendChild(ghostHint);

		ghost.appendChild(texts);

		document.body.appendChild(ghost);
		moveGhostTo(startX, startY);

		leftSidebarStore.setDraggingPlace(place);

		// Repart de zéro plutôt que de lire `hoveredShapeId` existant sur l'adapter tel quel : sans ça,
		// une forme survolée AVANT ce geste (curseur resté dessus depuis une interaction précédente)
		// pourrait sembler "visée" alors que le curseur n'a jamais retraversé la carte pendant CE
		// glisser-déposer précis.
		unsubscribeHover = currentEditorStore.get?.mapAdapter.on(event => {
			if (event.type !== 'shape:hovered') return;
			hoveredShapeId = event.shapeId ?? null;
			dropRefusal = hoveredShapeId ? getDropRefusal(hoveredShapeId) : null;
			updateGhostDropState();
		});
	};

	const handleMouseMove = (event: MouseEvent) => {
		if (!dragging) {
			if (Math.hypot(event.clientX - startX, event.clientY - startY) < DRAG_THRESHOLD_PX) return;
			startDragging();
		}

		moveGhostTo(event.clientX, event.clientY);
	};

	const handleMouseUp = () => {
		document.removeEventListener('mousemove', handleMouseMove);
		document.removeEventListener('mouseup', handleMouseUp);

		if (!dragging) return;

		ghost?.remove();
		unsubscribeHover?.();
		leftSidebarStore.setDraggingPlace(null);

		if (!hoveredShapeId) {
			message.warning(`Déposez le point d'intérêt sur une forme de la carte pour l'y lier.`);
			return;
		}

		// Re-vérifié au relâchement (et non lu depuis `dropRefusal`) : la forme a pu être verrouillée
		// entre-temps (ex. par un autre utilisateur) sans nouvel évènement `shape:hovered`.
		const refusal = getDropRefusal(hoveredShapeId);
		if (refusal) {
			message.warning(`${refusal} : impossible d'y lier le point d'intérêt.`);
			return;
		}

		linkPlaceToShape(hoveredShapeId, place)
			.then(() => message.success(`Le point d'intérêt « ${place.label} » a bien été lié à la forme.`))
			.catch(message.exception);
	};

	document.addEventListener('mousemove', handleMouseMove);
	document.addEventListener('mouseup', handleMouseUp);
}
